// pms-adapters / supabase / tillCatalogue — the till's carte (till brief §15–16, migration 20261019210100).
//
// Reads `pms_product` rows that have a role, and the three structure tables, and folds the structure into words the
// page shows (« asks Formule → Entrée », « choice of Choix sauce »). Writes are DIRECT under RLS, as for the stay's
// extras (D4): every rule — a priced item, an open price at 0 €, the TVA of the category, one rate per formule — is
// a constraint or a trigger on the row, so the database refuses a bad product whatever writes it.

import {
  err,
  ok,
  type ProductId,
  type Result,
  type ScopeSource,
  type TillCarte,
  type TillCatalogueRepository,
  type TillCategory,
  type TillColour,
  type TillProduct,
  type TillProductDraft,
  type TillRole,
} from '@lemontaigu/pms-core';
import { fromResult, fromVoid, scopedInsert, scopedQuery, toPmsError, type SupabaseClient } from './support.js';

/** A `pms_product` row as the carte reads it. */
export interface TillProductRow {
  id: number;
  code: string;
  label_fr: string;
  role: string;
  is_category: boolean;
  category_id: number | null;
  tva_rate: number | string | null;
  price_cents: number;
  till_screen: string | null;
  till_colour: string | null;
  ls_sku: string | null;
  vega_code: string | null;
  active: boolean;
  sort_order: number;
}

export interface TillStructureRows {
  groups: { id: number; label_fr: string; min_choices: number; max_choices: number }[];
  items: { group_id: number; product_id: number; sort_order: number }[];
  links: { product_id: number; group_id: number; sort_order: number }[];
  components: { product_id: number; component_id: number; required: boolean; sort_order: number }[];
}

const PRODUCT_COLUMNS =
  'id, code, label_fr, role, is_category, category_id, tva_rate, price_cents, till_screen, till_colour, ls_sku, ' +
  'vega_code, active, sort_order';

/** Pure: rows → the carte. Exported for its test. */
export function foldCarte(rows: TillProductRow[], s: TillStructureRows): TillCarte {
  const rate = (v: number | string | null) => Number(v ?? 0);
  const label = new Map(rows.map((r) => [r.id, r.label_fr]));
  const groupLabel = new Map(s.groups.map((g) => [g.id, g.label_fr]));
  const bySort = <T extends { sort_order: number }>(xs: T[]) => [...xs].sort((a, b) => a.sort_order - b.sort_order);

  const categories: TillCategory[] = rows
    .filter((r) => r.is_category)
    .map((r) => ({ id: r.id, code: r.code, labelFr: r.label_fr, tvaRate: rate(r.tva_rate) }))
    .sort((a, b) => a.code.localeCompare(b.code));

  const products: TillProduct[] = rows
    .filter((r) => !r.is_category && r.category_id !== null)
    .map((r) => ({
      id: r.id,
      code: r.code,
      labelFr: r.label_fr,
      role: r.role as TillRole,
      categoryId: r.category_id as number,
      tvaRate: rate(r.tva_rate),
      priceCents: r.price_cents,
      screen: r.till_screen,
      colour: (r.till_colour as TillColour | null) ?? null,
      lsSku: r.ls_sku,
      vegaCode: r.vega_code,
      active: r.active,
      sortOrder: r.sort_order,
      asks: bySort(s.links.filter((l) => l.product_id === r.id)).map((l) => groupLabel.get(l.group_id) ?? '?'),
      contains: bySort(s.components.filter((c) => c.product_id === r.id)).map((c) => ({
        labelFr: label.get(c.component_id) ?? '?',
        required: c.required,
      })),
      choiceOf: [
        ...new Set(s.items.filter((i) => i.product_id === r.id).map((i) => groupLabel.get(i.group_id) ?? '?')),
        ...s.components.filter((c) => c.component_id === r.id).map((c) => label.get(c.product_id) ?? '?'),
      ],
    }));
  return { categories, products };
}

export class SupabaseTillCatalogueRepository implements TillCatalogueRepository {
  constructor(
    private readonly db: SupabaseClient,
    private readonly scope: ScopeSource,
  ) {}

  async loadCarte(): Promise<Result<TillCarte>> {
    const scope = this.scope.current();
    const [p, g, i, l, c] = await Promise.all([
      scopedQuery(this.db.from('pms_product').select(PRODUCT_COLUMNS).not('role', 'is', null), scope),
      scopedQuery(this.db.from('pms_product_choice_group').select('id, label_fr, min_choices, max_choices'), scope),
      scopedQuery(this.db.from('pms_product_choice_item').select('group_id, product_id, sort_order'), scope),
      scopedQuery(this.db.from('pms_product_group_link').select('product_id, group_id, sort_order'), scope),
      scopedQuery(this.db.from('pms_product_component').select('product_id, component_id, required, sort_order'), scope),
    ]);
    for (const r of [p, g, i, l, c]) if (r.error) return { ok: false, error: toPmsError(r.error) };
    return ok(
      foldCarte((p.data ?? []) as unknown as TillProductRow[], {
        groups: (g.data ?? []) as TillStructureRows['groups'],
        items: (i.data ?? []) as TillStructureRows['items'],
        links: (l.data ?? []) as TillStructureRows['links'],
        components: (c.data ?? []) as TillStructureRows['components'],
      }),
    );
  }

  async upsertTillProduct(draft: TillProductDraft): Promise<Result<ProductId>> {
    // The TVA is the category's — read, never typed. The trigger refuses any other.
    const cat = await this.db
      .from('pms_product')
      .select('tva_rate')
      .eq('id', draft.categoryId)
      .eq('is_category', true)
      .maybeSingle();
    if (cat.error) return fromResult<ProductId>(cat);
    if (cat.data === null) return err('validation', 'Cette catégorie n\'existe pas.');
    const common = {
      label_fr: draft.labelFr.trim(),
      price_cents: draft.priceCents,
      category_id: draft.categoryId,
      tva_rate: Number((cat.data as { tva_rate: number | string }).tva_rate),
      till_screen: draft.screen?.trim() || null,
      till_colour: draft.colour,
      sort_order: draft.sortOrder,
      updated_at: new Date().toISOString(),
    };
    const q =
      draft.id === undefined
        ? this.db
            .from('pms_product')
            .insert({
              ...scopedInsert(this.scope.current()),
              ...common,
              // A key nobody types (§16): minted here, unique per établissement, never shown as an identity.
              code: `C${Date.now().toString(36).toUpperCase()}`,
              role: draft.role,
              kind: 'produit',
              applies_to: null,
              per_person: false,
            })
            .select('id')
            .single()
        : this.db.from('pms_product').update(common).eq('id', draft.id).not('role', 'is', null).select('id').single();
    const r = await q;
    if (r.error) return fromResult<ProductId>(r);
    return ok((r.data as { id: number }).id);
  }

  async setTillProductActive(id: ProductId, active: boolean): Promise<Result<void>> {
    return fromVoid(
      await this.db
        .from('pms_product')
        .update({ active, updated_at: new Date().toISOString() })
        .eq('id', id)
        .not('role', 'is', null),
    );
  }
}
